import React, { useCallback, useEffect, SetStateAction, Dispatch } from "react";
// @ts-ignore wait for react router update
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Promo,
  RaceProgram,
  RaceProgramRaceVideo,
  RaceVideoFeedback
} from "@tvg/ts-types/Race";
import getOptedInPromos from "@tvg/utils/PromoUtils";
import { NullaryFn } from "@tvg/ts-types/Functional";
import {
  Button,
  Countries,
  Flag,
  Header as Title,
  Icon,
  MtpPro
} from "@tvg/design-system";
import { get } from "lodash";
import { openExternalLink } from "@tvg/utils/mobileUtils";
import {
  getOldPromosURLs,
  getOptedInPromosStore
} from "../../../../store/selectors";
import {
  Container,
  TitleContainer,
  RaceSelectorWrapper,
  ButtonsWrapper
} from "./styled-components";
import { onClickWatchVideo, watchReplay } from "../utils";
import { isPromoExternal, getPromoUrl } from "../../../../utils/promos";
import { Status } from "../../../../types";
import VideoButton from "../../VideoButton";
import RaceSelector from "../../RaceSelector";

export interface MobileSubHeaderProps {
  country: Countries;
  title: string;
  isFavorite: boolean;
  onClickFavorite: () => void;
  video?: RaceProgramRaceVideo;
  videoFeedback?: RaceVideoFeedback;
  race: RaceProgram;
  races: Array<RaceProgram>;
  isLogged: boolean;
  mtp: number;
  status: Status;
  postTime: string;
  setIsMtpHidden: Dispatch<SetStateAction<boolean>>;
  onClickVideo: NullaryFn<void>;
  onClickPoolsNInfo: NullaryFn<void>;
}

const MobileSubHeader = ({
  country,
  title = "",
  videoFeedback,
  race,
  isLogged = false,
  mtp = 0,
  status,
  postTime,
  races,
  onClickVideo,
  onClickPoolsNInfo
}: MobileSubHeaderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const optedInPromos = useSelector(getOptedInPromosStore);
  const oldPromosURLs: string[] = useSelector(getOldPromosURLs);

  const promos: Promo | null = getOptedInPromos(
    get(race, "promos", []),
    optedInPromos,
    isLogged
  );

  const isExternal = isPromoExternal({ promos, oldPromosURLs });

  const handleClickWatchVideo = useCallback(
    () => onClickWatchVideo(dispatch, isLogged, race, videoFeedback),
    [race, watchReplay, isLogged]
  );

  useEffect(() => {
    if (searchParams.get("video") && !!race) {
      handleClickWatchVideo();
      searchParams.delete("video");
      setSearchParams(searchParams);
    }
  }, [searchParams, race]);

  return (
    <Container data-qa-label="track-sub-header-container">
      <TitleContainer>
        <Flag size="l" country={country} mr="space-5" qaLabel={country} />
        <Title tag="h1" qaLabel="race-title" color="blue.000" fontSize="xl">
          {title}
        </Title>
      </TitleContainer>
      <RaceSelectorWrapper>
        <MtpPro
          mtp={mtp}
          status={status}
          postTime={postTime}
          data-qa-label="mtp-status"
        />
        <RaceSelector races={races} isMobile />
        <ButtonsWrapper>
          <Button
            qaLabel="Info-button"
            icon={
              <Icon
                name="info"
                size="s"
                lineColor="blue_accent.600"
                backgroundColor="blue_accent.200"
                qaLabel="button-icon"
              />
            }
            onPress={onClickPoolsNInfo}
            variant="secondary_dark"
          >
            {!promos ? "Pools & Info" : <></>}
          </Button>
          {promos && (
            <Button
              onPress={() => {
                const promoUrl = getPromoUrl(isExternal, promos, race, true);
                return isExternal
                  ? openExternalLink(promoUrl)
                  : navigate(promoUrl);
              }}
              qaLabel={
                promos?.isOptedIn ? "promo-button" : "promo-button-opt-in"
              }
              icon={
                promos?.isOptedIn ? (
                  <Icon
                    name="optedIn"
                    size="s"
                    lineColor="green.600"
                    backgroundColor="green.100"
                    qaLabel="button-icon"
                  />
                ) : (
                  "promo"
                )
              }
              variant={promos?.isOptedIn ? "secondary_dark" : "marketing"}
            >
              <></>
            </Button>
          )}
        </ButtonsWrapper>
        <ButtonsWrapper withRightMargin>
          <VideoButton
            videoFeedback={videoFeedback}
            onClickWatchVideo={onClickVideo}
          />
        </ButtonsWrapper>
      </RaceSelectorWrapper>
    </Container>
  );
};

export default MobileSubHeader;
